import { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../services/axiosInstance';
import { backendPort } from '../components/Fungsiku';
import { useNotification } from './NotifPopup';

export function PageKontenUkuranBan({ token, authIdentity = {}, onMasterChanged }) {
  const [data, setData] = useState([]);
  const [mode, setMode] = useState('');
  const [nama, setNama] = useState('');
  const [selected, setSelected] = useState({ id: '', ukuran: '' });
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [saving, setSaving] = useState(false);
  const { showNotif } = useNotification();
  const canManage = String(authIdentity?.kode_cabang || '').trim().toUpperCase() === 'C-1';

  const loadData = useCallback(async () => {
    try {
      const res = await api.get(`${backendPort}/api/master/daftar_ukuran`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      showNotif('error', err.response?.data?.message || 'Gagal memuat daftar ukuran ban.');
    }
  }, [showNotif, token]);

  useEffect(() => {
    if (canManage) loadData();
  }, [canManage, loadData]);

  const filteredData = useMemo(() => data.filter((row) =>
    String(row?.ukuran || '').toLowerCase().includes(search.trim().toLowerCase())
  ), [data, search]);
  const totalPages = Math.max(1, Math.ceil(filteredData.length / pageSize));
  const paginatedData = filteredData.slice((page - 1) * pageSize, page * pageSize);
  const visiblePages = useMemo(() => {
    const maxVisible = 5;
    let startPage = Math.max(1, page - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);
    startPage = Math.max(1, endPage - maxVisible + 1);
    return Array.from({ length: endPage - startPage + 1 }, (_, index) => startPage + index);
  }, [page, totalPages]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const closeForm = () => {
    setMode('');
    setNama('');
    setSelected({ id: '', ukuran: '' });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (saving || (mode !== 'hapus' && !nama.trim())) return;
    setSaving(true);
    try {
      if (mode === 'tambah') {
        await api.post(`${backendPort}/api/master/tambah_ukuran`, { nama: nama.trim() });
        showNotif('success', 'Ukuran ban berhasil ditambahkan.');
      } else if (mode === 'edit') {
        await api.put(`${backendPort}/api/master/edit_ukuran`, { id: selected.id, nama: nama.trim() });
        showNotif('success', 'Ukuran ban berhasil diubah.');
      } else if (mode === 'hapus') {
        await api.delete(`${backendPort}/api/master/delete_ukuran/${selected.id}`);
        showNotif('success', 'Ukuran ban berhasil dihapus.');
      }
      closeForm();
      await loadData();
      if (typeof onMasterChanged === 'function') await onMasterChanged({ requestGroup: 'master-ukuran' });
    } catch (err) {
      showNotif('error', err.response?.data?.message || 'Perubahan ukuran ban gagal.');
    } finally {
      setSaving(false);
    }
  };

  if (!canManage) {
    return <div className="alert alert-danger">Pengelolaan ukuran ban hanya dapat diakses oleh cabang pusat.</div>;
  }

  const openForm = (nextMode, row = { id: '', ukuran: '' }) => {
    setMode(nextMode);
    setSelected(row);
    setNama(nextMode === 'edit' ? row.ukuran : '');
  };
  const title = mode === 'tambah' ? 'Tambah Ukuran Ban' : mode === 'edit' ? 'Edit/Koreksi Ukuran Ban' : 'Hapus Ukuran Ban';

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-md-7">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center" style={{ background: '#09314F', color: '#fff' }}>
              Daftar Ukuran Ban
              <button className="btn btn-outline-secondary ls" style={{ fontSize: 'large', color: '#fff' }} onClick={() => openForm('tambah')}>Tambah <i className="fa fa-add" /></button>
            </div>
            <div className="card-body p-0">
              <div className="row my-2 mx-1">
                <div className="col-md-4"><input className="form-control" placeholder="Cari ukuran..." value={search} onChange={(event) => { setSearch(event.target.value); setPage(1); }} /></div>
                <div className="col-md-4" />
                <div className="col-md-4 text-end">
                  <label className="form-label">Show</label>
                  <select className="form-select w-auto d-inline-block ms-2" value={pageSize} onChange={(event) => { setPageSize(Number(event.target.value)); setPage(1); }}>
                    {[5, 10, 20].map((value) => <option key={value} value={value}>{value}</option>)}
                  </select>
                </div>
              </div>
              <table className="table table-bordered table-hover mb-0">
                <thead><tr><th>Kode</th><th>Ukuran Ban</th><th width="80">Aksi</th></tr></thead>
                <tbody>
                  {paginatedData.map((row) => <tr key={row.id}>
                    <td>{`UB-${row.id}`}</td><td>{row.ukuran}</td>
                    <td className="d-flex justify-content-between align-items-center">
                      <span title="Ubah" onClick={() => openForm('edit', row)} style={{ cursor: 'pointer' }}><i className="fa fa-folder-open" /></span>
                      <span title="Hapus" onClick={() => openForm('hapus', row)} style={{ cursor: 'pointer' }}><i className="fa fa-trash text-red-500" /></span>
                    </td>
                  </tr>)}
                  {!filteredData.length && <tr><td colSpan="3" className="text-center">Belum ada data.</td></tr>}
                </tbody>
              </table>
            </div>
            <div className="d-flex justify-content-between align-items-center my-2 mx-3">
              <div>Showing {filteredData.length ? (page - 1) * pageSize + 1 : 0} to {Math.min(page * pageSize, filteredData.length)} of {filteredData.length} entries</div>
              <nav>
                <ul className="pagination mb-0">
                  <li className={`page-item ${page === 1 ? 'disabled' : ''}`}><button type="button" className="page-link" onClick={() => setPage(1)} disabled={page === 1}>←</button></li>
                  {visiblePages.map((value) => <li key={value} className={`page-item ${page === value ? 'active' : ''}`}><button type="button" className="page-link" onClick={() => setPage(value)}>{value}</button></li>)}
                  <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}><button type="button" className="page-link" onClick={() => setPage(totalPages)} disabled={page === totalPages}>→</button></li>
                </ul>
              </nav>
            </div>
          </div>
        </div>

        {mode && <div className="col-md-4">
          <div className="card">
            <div className="card-header" style={{ background: mode === 'hapus' ? '#dc3545' : '#09314F', color: '#fff' }}>{title}</div>
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                {mode !== 'tambah' && <div className="mb-3"><label>Kode</label><input className="form-control" value={`UB-${selected.id}`} disabled /></div>}
                <div className="mb-3">
                  <label>Ukuran Ban</label>
                  <input className="form-control" value={mode === 'hapus' ? selected.ukuran : nama} onChange={(event) => setNama(event.target.value)} disabled={mode === 'hapus'} autoFocus={mode !== 'hapus'} />
                </div>
                {mode === 'hapus' && <p className="text-muted small">Penghapusan ditolak bila ukuran ini masih digunakan pada data ban.</p>}
                <button className={`btn me-2 ${mode === 'hapus' ? 'btn-danger' : 'btn-primary'}`} disabled={saving || (mode !== 'hapus' && !nama.trim())}>{saving ? 'Memproses...' : mode === 'hapus' ? 'Delete' : mode === 'edit' ? 'Update' : 'Simpan'}</button>
                <button type="button" className="btn btn-success" onClick={closeForm} disabled={saving}>Batal</button>
              </form>
            </div>
          </div>
        </div>}
      </div>
    </div>
  );
}
